<template>
  <!-- Fixed progress bar — hidden on gender step since it's a pre-questionnaire screen -->
  <div class="t1-progress-bar" v-if="!onGenderStep">
    <v-progress-linear
      :model-value="totalProgress"
      color="success"
      height="4"
    />
    <div class="t1-progress-label text-caption text-center text-medium-emphasis py-1">
      Secțiunea {{ currentSectionIndex + 1 }} din {{ sections.length }}
    </div>
  </div>

  <v-container class="py-6" style="max-width: 680px;">

    <!-- Gender step: shown before the questionnaire sections -->
    <template v-if="onGenderStep">
      <v-chip color="success" variant="tonal" size="small" class="mb-3">Despre tine</v-chip>
      <p class="text-body-2 text-medium-emphasis mb-6">
        Răspunsurile la chestionar sunt formulate diferit în funcție de gen.
        Alege varianta care ți se potrivește.
      </p>

      <v-card rounded="lg" elevation="1" class="pa-6 mb-4" :class="{ 'card-unanswered': submitted && !gender }">
        <p class="text-body-1 font-weight-medium mb-4">Care este genul tău?</p>
        <v-radio-group v-model="gender" hide-details color="success">
          <v-radio label="Masculin" value="male" color="success" class="mb-2" />
          <v-radio label="Feminin"  value="female" color="success" />
        </v-radio-group>
      </v-card>

      <div class="d-flex justify-end mt-6 mb-10">
        <v-btn color="success" append-icon="mdi-arrow-right" @click="confirmGender">
          Continuă
        </v-btn>
      </div>
    </template>

    <!-- Questionnaire sections -->
    <template v-else>

      <!-- Section header -->
      <div class="d-flex align-center gap-2 mb-3">
        <v-chip color="success" variant="tonal" size="small">
          {{ currentSection.label }}
        </v-chip>
        <v-btn v-if="isDevelopment" variant="tonal" color="warning" size="x-small" @click="prefill">
          Dev: Prefill
        </v-btn>
      </div>
      <p class="text-body-2 text-medium-emphasis mb-6">
        {{ currentSection.instruction }}
      </p>

      <!-- One card per question, all visible at once -->
      <v-card
        v-for="question in currentSection.items"
        :key="question.id"
        rounded="lg"
        elevation="1"
        class="pa-6 mb-4"
        :class="{ 'card-unanswered': submitted && answers[question.id] == null }"
        :data-question-id="question.id"
      >
        <p class="text-body-1 font-weight-medium mb-4" style="line-height: 1.6;">
          {{ gender === 'female' && question.textFemale ? question.textFemale : question.text }}
        </p>

        <v-radio-group v-model="answers[question.id]" hide-details color="success">
          <v-radio
            v-for="(label, idx) in currentSection.scaleLabels"
            :key="idx"
            :label="`${currentSection.scaleMin + idx} — ${label}`"
            :value="currentSection.scaleMin + idx"
            color="success"
            class="mb-1"
          />
        </v-radio-group>
      </v-card>

      <!-- Navigation -->
      <div class="d-flex justify-space-between mt-6 mb-10">
        <v-btn
          variant="text"
          prepend-icon="mdi-arrow-left"
          @click="goPrev"
        >
          Înapoi
        </v-btn>

        <v-btn
          v-if="!isLastSection"
          color="success"
          append-icon="mdi-arrow-right"
          @click="goNext"
        >
          Continuă
        </v-btn>

        <v-btn
          v-else
          color="success"
          :loading="saving"
          append-icon="mdi-check"
          @click="saveAnswers"
        >
          Salvează
        </v-btn>
      </div>

    </template>

  </v-container>
</template>

<script>
import { appStore } from '@/stores/AppStore'
import apiService from '@/services/ApiService'
import questionsData from '@/data/t1-questions.json'
import envMixin from '@/mixins/envMixin'

export default {
  name: 'ProfileT1View',
  mixins: [envMixin],

  data() {
    return {
      onGenderStep: true,   // shown first, before questionnaire sections
      gender: null,         // 'male' | 'female' — required to select PVQ-21 pronoun version
      currentSectionIndex: 0,
      answers: {},
      saving: false,
      submitted: false,
    }
  },

  computed: {
    store() {
      return appStore()
    },

    sections() {
      return questionsData.sections
    },

    currentSection() {
      return this.sections[this.currentSectionIndex]
    },

    isLastSection() {
      return this.currentSectionIndex === this.sections.length - 1
    },

    isSectionComplete() {
      return this.currentSection.items.every(q => this.answers[q.id] != null)
    },

    totalProgress() {
      const total = this.sections.reduce((sum, s) => sum + s.items.length, 0)
      const answered = Object.keys(this.answers).length
      return (answered / total) * 100
    },
  },

  methods: {
    // Validates gender selection and advances to the questionnaire sections
    confirmGender() {
      this.submitted = true
      if (!this.gender) return
      this.submitted = false
      this.onGenderStep = false
      window.scrollTo(0, 0)
    },

    goNext() {
      this.submitted = true
      if (!this.isSectionComplete) {
        this.scrollToFirstUnanswered()
        return
      }
      this.submitted = false
      window.scrollTo(0, 0)
      this.currentSectionIndex++
    },

    goPrev() {
      this.submitted = false
      window.scrollTo(0, 0)
      // Go back to gender step from the first questionnaire section
      if (this.currentSectionIndex === 0) {
        this.onGenderStep = true
      } else {
        this.currentSectionIndex--
      }
    },

    scrollToFirstUnanswered() {
      const firstUnanswered = this.currentSection.items.find(q => this.answers[q.id] == null)
      if (!firstUnanswered) return
      this.$nextTick(() => {
        const el = document.querySelector(`[data-question-id="${firstUnanswered.id}"]`)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    },

    prefill() {
      const filled = {}
      questionsData.sections.forEach(section => {
        section.items.forEach((item, idx) => {
          // Cycle through the scale to avoid the no-discrimination quality check
          filled[item.id] = section.scaleMin + (idx % (section.scaleMax - section.scaleMin + 1))
        })
      })
      this.answers = filled
    },

    async saveAnswers() {
      this.submitted = true
      if (!this.isSectionComplete) {
        this.scrollToFirstUnanswered()
        return
      }
      this.saving = true
      try {
        const data = await apiService.submitT1Answers(this.answers, this.gender)
        this.store.setUser({ ...this.store.user, profileState: data.profileState })
        this.$router.push('/me')
      } catch (err) {
        console.error('T1 save error:', err)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.card-unanswered {
  border: 2px solid rgb(var(--v-theme-error)) !important;
}

.t1-progress-bar {
  position: sticky;
  top: 0;
  z-index: 99;
  background: rgb(var(--v-theme-surface));
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
